import {Group} from '@mantine/core';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {capwords} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

interface BaseProps {
  breadcrumbs: Array<{name: string; url: string}>;
  children: ReactNode;
  title?: string;
  type?: string;
  onExport?: () => void;
  dataTestAttrs?: DataTestAttrs;
}

type SettingsShownProps =
  | {isSettingsShown: boolean; onChangeIsSettingsShown: (isSettingsShown: boolean) => void}
  | {isSettingsShown?: never; onChangeIsSettingsShown?: never};

export type SettingsPageProps = BaseProps & SettingsShownProps;

function ShowSettingsButton({
  toggleShowSettingsButton,
  isSettingsShown,
  type,
}: {
  toggleShowSettingsButton: () => void;
  isSettingsShown: boolean;
  type?: string;
}): JSX.Element {
  const text = `Manage ${capwords(type)}`;
  return (
    <Tooltip text={text}>
      <a
        className={classNames('settingsPage-btn', {
          'is-active': isSettingsShown,
        })}
        onClick={toggleShowSettingsButton}
        {...getTestProps({component: 'ShowSettingsButton', element: 'root'})}
      >
        <BenchlingIcon altText={text} icon="gear" />
      </a>
    </Tooltip>
  );
}

function SettingsPage({
  breadcrumbs,
  children,
  dataTestAttrs,
  isSettingsShown,
  title,
  type,
  onChangeIsSettingsShown,
  onExport,
}: SettingsPageProps): JSX.Element {
  return (
    <div className="settingsPage" {...getTestProps(dataTestAttrs)}>
      <div className="settingsPage-contentWrapper">
        <Group align="start" position="apart">
          <h2 className="settingsPage-header">
            {breadcrumbs.map(({name, url}) => [
              <a
                className="settingsPage-header-crumb"
                key="name"
                href={url}
                onClick={(e: ElementMouseEvent) => {
                  benchlingUtil.navigate(e);
                }}
              >
                {name}
              </a>,
              <BenchlingIcon className="settingsPage-header-chevron" key="chevron" icon="chevron-right" />,
            ])}
            <span className={classNames('settingsPage-header-title', {faded: !title})}>
              {title != null ? title : '...'}
            </span>
          </h2>
          {(onExport != null || isSettingsShown != null) && (
            <Group grow={false} spacing={0}>
              {onExport && (
                <Tooltip text="Export">
                  <a className="settingsPage-btn" onClick={onExport}>
                    <BenchlingIcon altText="Export" icon="export" />
                  </a>
                </Tooltip>
              )}
              {isSettingsShown != null && (
                <ShowSettingsButton
                  type={type}
                  toggleShowSettingsButton={() => {
                    onChangeIsSettingsShown(true);
                  }}
                  isSettingsShown={isSettingsShown}
                />
              )}
            </Group>
          )}
        </Group>
        <div className="settingsPage-content">{children}</div>
      </div>
    </div>
  );
}

SettingsPage.defaultProps = {
  breadcrumbs: [],
};

export default SettingsPage;
